import { useCallback, useRef, useState } from "react"
import { closeSnackbar, enqueueSnackbar, type SnackbarKey } from "notistack"

import { ErrorDisplay } from "../../../../../core/api/errorTypes"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useAppLocalization } from "../../../../../modules/localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { fetchEventsForServiceCenter } from "../../createOrderApi"
import { useCreateOrderForm } from "../../mainForm/CreateOrderFormContext"

import { NullableCalendarEvent, TimelineCalendarEvent, TimelineCalendarResource } from "./calendar/fullCalendarTypes"
import { useRefreshCalendar } from "./context/RefreshCalendarProvider"
import { hasEventOverlap, toTimelineEvents } from "./helpers"
import { useResourceBookingDraft } from "./ResourceBookingDraftContext"

type UseBookingAvailabilityGuardArgs = Readonly<{
    resources: ReadonlyArray<TimelineCalendarResource>
}>

const NO_BUSY_RESOURCES: ReadonlySet<string> = new Set()

export type DraftAvailability = "available" | "taken" | "superseded"

/** Blocks a booking the timeline still draws as free. Goes to the network, not the events cache. */
export function useBookingAvailabilityGuard({ resources }: UseBookingAvailabilityGuardArgs) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const { user, orderContext, flowConfig } = useCreateOrderForm()
    const { triggerRefetch } = useRefreshCalendar()
    const { fullResources, setDraftEvent, initialEventStateId, handleChangeInitEventState } = useResourceBookingDraft()
    const [isChecking, setIsChecking] = useState(false)
    const latestCheckRef = useRef(0)
    const takenSnackbarKeyRef = useRef<SnackbarKey | undefined>(undefined)

    const { mutateAsync: callFetchEvents } = useApiMutation(fetchEventsForServiceCenter, {
        errorDisplay: ErrorDisplay.NONE,
    })

    const userId = user.userId
    // same expression as the timeline's `localEventUpdateId`, so both agree which order is being edited
    const excludeOrderId = initialEventStateId || orderContext.orderId
    const isOutlookFlow = !!flowConfig?.flow

    /** Fails open — an unreachable check must not block a booking the backend would accept. */
    const findBusyResources = useCallback(
        async (event: TimelineCalendarEvent): Promise<ReadonlySet<string>> => {
            const draftResources = resources.filter((resource) => event.resourceIds.includes(resource.id))
            if (!draftResources.length) return NO_BUSY_RESOURCES

            const serviceCenterIds = Array.from(
                new Set(draftResources.map((resource) => resource.locationId.split("_")[0]).filter(Boolean)),
            )
            if (!serviceCenterIds.length) return NO_BUSY_RESOURCES

            // widened so a neighbour that collides only through its buffer is still returned
            const maxBuffer = Math.max(0, ...draftResources.map((resource) => resource.buffer ?? 0))
            const startDate = dateTimeFactory.toApiISOMinutesFormat(
                dateTimeFactory.fromISO(event.start).minus({ minutes: maxBuffer }),
            )
            const endDate = dateTimeFactory.toApiISOMinutesFormat(
                dateTimeFactory.fromISO(event.end).plus({ minutes: maxBuffer }),
            )

            try {
                const responses = await Promise.all(
                    serviceCenterIds.map((serviceCenterId) =>
                        callFetchEvents({ userId, serviceCenterId, startDate, endDate }),
                    ),
                )

                const excludedEventId = excludeOrderId ? String(excludeOrderId) : undefined
                const serverEvents = responses
                    .flatMap((response) => toTimelineEvents(response, dateTimeFactory))
                    .filter((serverEvent) => serverEvent.id !== excludedEventId)

                const busyResources = draftResources.filter((resource) =>
                    hasEventOverlap({
                        start: event.start,
                        end: event.end,
                        buffer: resource.buffer ?? 0,
                        events: serverEvents.filter((serverEvent) => serverEvent.resourceIds.includes(resource.id)),
                        dateTimeFactory,
                    }),
                )

                return new Set(busyResources.map((resource) => resource.id))
            } catch {
                return NO_BUSY_RESOURCES
            }
        },
        [callFetchEvents, dateTimeFactory, excludeOrderId, resources, userId],
    )

    /** Losing the last resource follows the same rule as deselecting it by hand. */
    const releaseBusyResources = useCallback(
        (draft: TimelineCalendarEvent, busyResourceIds: ReadonlySet<string>) => {
            const remainingResourceIds = draft.resourceIds.filter((resourceId) => !busyResourceIds.has(resourceId))

            if (remainingResourceIds.length) {
                setDraftEvent(
                    { ...draft, resourceIds: remainingResourceIds },
                    fullResources.filter((resource) => remainingResourceIds.includes(String(resource.id))),
                )
                return
            }

            if (isOutlookFlow) {
                setDraftEvent({ ...draft, resourceIds: [] }, [])
                return
            }

            setDraftEvent(null)
            // a null draft never flags isUpdatedOrder, and without it there is no "Undo all"
            if (initialEventStateId) handleChangeInitEventState({ isUpdated: true })
        },
        [fullResources, handleChangeInitEventState, initialEventStateId, isOutlookFlow, setDraftEvent],
    )

    const ensureDraftStillAvailable = useCallback(
        async (draft: NullableCalendarEvent): Promise<DraftAvailability> => {
            if (!draft) return "available"

            const check = ++latestCheckRef.current
            setIsChecking(true)
            let busyResourceIds: ReadonlySet<string>
            try {
                busyResourceIds = await findBusyResources(draft)
            } finally {
                if (check === latestCheckRef.current) setIsChecking(false)
            }

            if (check !== latestCheckRef.current) return "superseded"
            if (!busyResourceIds.size) return "available"

            releaseBusyResources(draft, busyResourceIds)
            if (takenSnackbarKeyRef.current !== undefined) closeSnackbar(takenSnackbarKeyRef.current)
            takenSnackbarKeyRef.current = enqueueSnackbar({
                variant: "warning",
                message: l10n.getString("create-order-timeline-slot-taken-message"),
                // replace instead of merge: the global preventDuplicate would swallow a repeat
                preventDuplicate: false,
            })
            triggerRefetch()
            return "taken"
        },
        [findBusyResources, l10n, releaseBusyResources, triggerRefetch],
    )

    return { ensureDraftStillAvailable, isChecking }
}
